import React from 'react';
import '../styles/LegalDocs.css';

const CookiesPolicy = () => {
  return (
    <div className="legal-doc-container">
      <div className="legal-doc">
        <div className="doc-header">
          <h1>DRIVVE CARPOOLING PLATFORM</h1>
          <h2>COOKIES &amp; TRACKING TECHNOLOGIES POLICY</h2>
          <div className="doc-meta">
            <p><strong>Last Updated:</strong> June 11, 2026</p>
            <p><strong>Effective Date:</strong> Immediate upon Interaction or Sign-up</p>
            <p><strong>Framework:</strong> IT Act, 2000 &amp; PDPP Act, 2023</p>
            <p><strong>Environment:</strong> Mobile SDKs &amp; Core APIs</p>
          </div>
        </div>

        <div className="doc-section">
          <h3 className="notice-heading">STATUTORY COMPLIANCE NOTICE:</h3>
          <p>
            This policy outlines the operational deployment of cookies, local storage systems, application program identifiers, and Software Development Kits (SDKs) utilized across the DRIVVE platform. It satisfies transparency and information parameters mandated under the Information Technology Act, 2000 and the Digital Personal Data Protection (PDPP) Act, 2023 (Government of India).
          </p>
          <p>
            The founding promoters, developers, and owners operating under the trade name "DRIVVE" (collectively, the "Platform Owners", "We", "Us", or "Our") employ cookies, mobile advertising tags, tracking pixels, automated scripts, local memory states, and Software Development Kits (SDKs) (collectively, "Tracking Technologies") to safely optimize user states, regulate session tokens, manage preferences, and secure our peer-to-peer connection networks. This policy applies uniformly to all software logs, internal builds, and API queries across our digital environment.
          </p>
        </div>

        <div className="doc-section">
          <h3>1. OPERATIONAL STATUS &amp; ADAPTIVE APPLICATION SUCCESSION</h3>
          
          <h4>1.1 Unregistered Brand Status Warning</h4>
          <p>
            You explicitly acknowledge, verify, and understand that as of the current Effective Date, "DRIVVE" is an unregistered proprietary trade name and mobile computing system managed directly by its founding promoters, and is not yet incorporated as an independent corporate entity. All associated data systems, technical assets, tracking configurations, and telemetry repositories rest exclusively with the Platform Owners.
          </p>

          <h4>1.2 Automatic Corporate Assignment</h4>
          <p>
            You explicitly agree that immediately upon formal incorporation under the Indian Companies Act, 2013, all data records, tracing histories, tracking profiles, system logs, and consent properties managed under this policy shall automatically assign, transfer, and novate to the newly established corporate entity (e.g., DRIVVE Private Limited or its authorized corporate equivalent) without requiring prior notices or supplementary approvals.
          </p>
        </div>

        <div className="doc-section">
          <h3>2. THE NATURE OF MOBILE SDKS VS. BROWSER COOKIES</h3>
          <p>
            Because the DRIVVE platform is delivered primarily as compiled mobile application software code, traditional browser-centric text files ("cookies") are largely replaced by specialized local Software Development Kits (SDKs), local storage segments, and secure device tokens. These technological segments run natively within your mobile environment to secure active user sessions, track interface parameters, verify identity markers, record diagnostic configurations, and handle real-time geographic mapping streams. Standard cookies may still be deployed when accessing our web-based domains, dashboard links, or customer support pages.
          </p>
        </div>

        <div className="doc-section">
          <h3>3. CATEGORIZATION OF DEPLOYED TRACKING TECHNOLOGIES</h3>
          <p>
            DRIVVE restricts its tracking parameters strictly to the operational and safety essentials needed to run a non-commercial carpooling matching platform. The tracking elements are categorized as follows:
          </p>
          <ul className="doc-list">
            <li>
              <strong>Strictly Necessary &amp; Essential SDKs:</strong> Mandatory tracking profiles required for basic app execution, account access controls, and session management. These securely hold your active authentication tokens, verify user login states, and run active threat-detection scripts (such as data-scraping throttles) to block automated bot arrays from accessing user profiles or disrupting the platform.
            </li>
            <li>
              <strong>Performance, Diagnostics &amp; Telemetry:</strong> Anonymized tracking tokens and crash reporting tools that monitor technical operations. These identify application crashes, slow loading screens, hardware compatibility issues, battery consumption statistics, and server response latencies, allowing our developers to optimize performance and remove technical bottlenecks.
            </li>
            <li>
              <strong>Functional Preference Memory:</strong> Code tokens and application flags that remember choices made via our manual Request-and-Accept model. These cache your saved home/work coordinates, routine destinations, language options, notification parameters, and regular commute shift timings to stop repetitive, manual data re-entry.
            </li>
            <li>
              <strong>Active Sensor Location Identifiers:</strong> DRIVVEn directly by hardware-level Global Positioning System (GPS) permissions, this module triggers to capture precise location, movement direction, and velocity parameters exclusively during active ride lifecycles to operationalize our Live Tracking Feature for matched peers.
            </li>
          </ul>
        </div>

        <div className="doc-section">
          <h3>4. THIRD-PARTY SDK INTEGRATIONS</h3>
          <p>
            To guarantee on-road mapping accuracy, maintain communications, and protect transactional security, DRIVVE integrates verified, industry-standard external technical libraries on our platform. These include:
          </p>
          <ol className="doc-list">
            <li>
              <strong>Google Maps SDK / API:</strong> Utilized to calculate optimal route paths, generate pick-up circles, display interactive geography, provide route recommendations, and stream real-time location telemetry to your matched carpool peer.
            </li>
            <li>
              <strong>PCI-DSS Compliant Payment Aggregator SDKs:</strong> Embedded via secure, sandboxed frames to route peer cost-sharing contributions or technology convenience fees safely, ensuring that raw UPI keys, wallet details, or bank card credentials are never exposed to or stored inside DRIVVE's production databases.
            </li>
            <li>
              <strong>Cloud &amp; Support Analytics Libraries:</strong> Supplementary verified scripts used to manage technical database queries, push notifications, and customer support ticket integrations.
            </li>
          </ol>
        </div>

        <div className="doc-section">
          <h3>5. RETENTION CAPPED LIMITS &amp; THE PURGE MANDATE</h3>
          <p>
            In absolute alignment with the principle of Data Minimization under the PDPP Act, 2023, DRIVVE enforces strict architectural limits on tracking data retention:
          </p>
          <ul className="doc-list">
            <li>
              <strong>System Log &amp; Performance Tokens:</strong> General log footprints, diagnostics, and aggregated performance tracking tokens are kept for a maximum historical duration of 24 months, after which they are automatically erased or completely anonymized.
            </li>
            <li>
              <strong>Live Location Tracking Telemetry:</strong> Raw, real-time live tracking GPS coordinates are completely cut off and disconnected the exact millisecond a ride reaches its destination and matching users execute final drop-off validation handshakes. Background geographic tracking is explicitly blocked when you are not actively engaged in a carpooling lifecycle.
            </li>
          </ul>
        </div>

        <div className="doc-section">
          <h3>6. PROGRESSIVE MARKETPLACE AVAILABILITY BLUEPRINT</h3>
          <p>
            The DRIVVE mobile software utility is distributed progressively across digital marketplaces. The initial build is deployed exclusively via the Google Play Store for devices running on the Android software framework. Build versions for the Apple App Store targeting iOS systems are scheduled to deploy approximately three (3) months following the initial Android rollout. This tracking policy governs uniformly across both ecosystems, adapting automatically to the tracking and permission architecture of each device manufacturer.
          </p>
        </div>

        <div className="doc-section">
          <h3>7. USER CONTROLS, PRIVACY SIGNALS &amp; MANAGEMENT</h3>

          <h4>7.1 Device and Application Settings</h4>
          <p>
            Users maintain complete command over tracking, application permissions, and telemetry profiles via native device setting layers. On Android devices, users can navigate to Settings → Privacy → Ads to reset advertising indices. On our upcoming iOS builds, the platform will display the mandatory App Tracking Transparency (ATT) framework prompt to manage cross-application tracking permissions.
          </p>

          <h4>7.2 "Do Not Track" (DNT) Notice</h4>
          <p>
            Some internet browsers support "Do Not Track" signals. Because no universally accepted technical standard currently exists for responding to DNT signals across mobile application APIs and web structures, DRIVVE does not adjust its core operational data collection practices in response to generic browser Do Not Track requests.
          </p>

          <h4>7.3 Impact of Disabling Tracking Technologies</h4>
          <p>
            While you are free to restrict functional preferences, analytics, and advertising tokens, please note that disabling essential location identifiers, secure authentication tokens, or security tracking SDKs will cause the app's matchmaking software, manual pair coordination, and live tracking safety features to stop working entirely.
          </p>
        </div>

        <div className="doc-section">
          <h3>8. COMPLIANCE CELL &amp; CONTACT CHANNELS</h3>
          <p>
            DRIVVE may update this Cookies &amp; Tracking Technologies Policy periodically to reflect infrastructure improvements or regulatory upgrades. For any technical queries, cookie preferences, or data privacy inquiries regarding tracking code implementations, you can contact our compliance and technical data desk directly:
          </p>

          <table className="contact-table">
            <tbody>
              <tr>
                <td><strong>Primary Compliance Support</strong></td>
                <td>Promoter Data and Infrastructure Group</td>
              </tr>
              <tr>
                <td><strong>Technical Privacy Lane</strong></td>
                <td><a href="mailto:privacy@drivve.in">privacy@drivve.in</a></td>
              </tr>
              <tr>
                <td><strong>Legal Escalation Lane</strong></td>
                <td><a href="mailto:legal@drivve.in">legal@drivve.in</a></td>
              </tr>
              <tr>
                <td><strong>Official Web Domain</strong></td>
                <td><a href="http://www.drivve.in">www.drivve.in</a></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicy;